'use client';

import { useState, useEffect } from 'react';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X } from 'lucide-react';

interface Plan {
	id: string;
	name: string;
	credits: number;
	price: number;
	description?: string;
	isActive: boolean;
	isRecurring: boolean;
}

interface CurrentSubscription {
	id: string;
	plan: Plan;
	status: string;
}

interface QuotaUpgradeModalProps {
	isOpen: boolean;
	onClose: () => void;
	siteId: string;
	quotaType: 'lead' | 'email';
	currentSubscription?: CurrentSubscription;
	onPlanChanged: () => void;
	title?: string;
	description?: string;
}

export function QuotaUpgradeModal({
	isOpen,
	onClose,
	siteId,
	quotaType,
	currentSubscription,
	onPlanChanged,
	title,
	description
}: QuotaUpgradeModalProps) {
	const [plans, setPlans] = useState<Plan[]>([]);
	const [selectedPlan, setSelectedPlan] = useState<string>('');
	const [loading, setLoading] = useState(false);
	const [upgrading, setUpgrading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const modalTitle = title || `${quotaType === 'lead' ? 'Lead' : 'Email'} Quota Plans`;
	const modalDescription =
		description ||
		`Choose a plan that fits your ${quotaType === 'lead' ? 'lead generation' : 'email sending'} needs.`;

	useEffect(() => {
		if (isOpen) {
			fetchPlans();
		}
	}, [isOpen, quotaType]);

	const fetchPlans = async () => {
		try {
			setLoading(true);
			setError(null);

			// Use different endpoints based on quota type
			const endpoint =
				quotaType === 'lead'
					? `/api/sites/${siteId}/lead-quota/plans`
					: `/api/sites/${siteId}/email-quota/plans`;

			const response = await fetch(endpoint);
			const data = await response.json();

			if (data.success) {
				setPlans(data.plans || []);

				// Set current plan as selected if exists
				if (currentSubscription?.plan?.id) {
					setSelectedPlan(currentSubscription.plan.id);
				} else {
					// If no current subscription, default to Free plan
					const freePlan = (data.plans || []).find((plan: Plan) => plan.name === 'Free');
					if (freePlan) {
						setSelectedPlan(freePlan.id);
					}
				}
			} else {
				setError(data.error || 'Failed to fetch plans');
			}
		} catch (err) {
			console.error('Error fetching plans:', err);
			setError('Failed to load plans');
		} finally {
			setLoading(false);
		}
	};

	const handleUpgrade = async () => {
		if (!selectedPlan) return;

		try {
			setUpgrading(true);
			setError(null);

			// Use different endpoints based on quota type
			const endpoint =
				quotaType === 'lead'
					? `/api/sites/${siteId}/lead-quota/upgrade`
					: `/api/sites/${siteId}/email-quota/upgrade`;

			const response = await fetch(endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					planId: selectedPlan
				})
			});

			const result = await response.json();

			if (result.success) {
				// Check if we need to redirect to purchase page
				if (result.redirectTo) {
					window.location.href = result.redirectTo;
					return;
				}

				onPlanChanged();
				onClose();
			} else {
				setError(result.error || 'Failed to upgrade plan');
			}
		} catch (err) {
			console.error('Error upgrading plan:', err);
			setError('Failed to upgrade plan');
		} finally {
			setUpgrading(false);
		}
	};

	if (!isOpen) return null;

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
				<DialogHeader>
					<DialogTitle className="text-2xl font-bold">{modalTitle}</DialogTitle>
					<DialogDescription className="text-gray-600">
						{modalDescription}
					</DialogDescription>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto max-h-[calc(90vh-200px)]">
					{error && (
						<div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
							<span className="text-red-700 text-sm">{error}</span>
						</div>
					)}

					{loading ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
						</div>
					) : (
						<div className="space-y-6">
							{/* Current Plan Info */}
							{(currentSubscription ||
								(!currentSubscription && plans.find((p) => p.name === 'Free'))) && (
								<Card className="bg-emerald-50 border-emerald-200">
									<CardContent className="p-4">
										<div className="flex items-center justify-between">
											<div>
												<div className="font-semibold text-emerald-900">
													Current Plan:{' '}
													{currentSubscription?.plan?.name || 'Free'}
												</div>
												<div className="text-sm text-emerald-700">
													${currentSubscription?.plan?.price || 0}/month •{' '}
													{(
														currentSubscription?.plan?.credits ||
														plans.find((p) => p.name === 'Free')
															?.credits ||
														0
													).toLocaleString()}{' '}
													{quotaType === 'lead' ? 'leads' : 'emails'}
												</div>
											</div>
											<Badge
												variant="secondary"
												className="bg-emerald-100 text-emerald-800"
											>
												Active
											</Badge>
										</div>
									</CardContent>
								</Card>
							)}

							{/* Available Plans */}
							<div>
								<h3 className="text-lg font-semibold mb-4">Available Plans</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
									{plans.map((plan) => {
										const isCurrentPlan =
											plan.id === currentSubscription?.plan?.id ||
											(!currentSubscription && plan.name === 'Free');
										const isSelected = selectedPlan === plan.id;

										return (
											<Card
												key={plan.id}
												className={`cursor-pointer transition-all duration-200 ${
													isSelected
														? 'ring-2 ring-emerald-500 border-emerald-500 shadow-md'
														: 'hover:border-emerald-300 hover:shadow-sm'
												} ${isCurrentPlan ? 'bg-emerald-50' : ''}`}
												onClick={() => setSelectedPlan(plan.id)}
											>
												<CardContent className="p-4">
													<div className="space-y-3">
														<div className="flex items-center justify-between">
															<h3 className="font-semibold text-lg">
																{plan.name}
															</h3>
															{isCurrentPlan && (
																<Badge
																	variant="outline"
																	className="text-emerald-700 border-emerald-300"
																>
																	Current
																</Badge>
															)}
														</div>

														<div className="text-2xl font-bold text-gray-900">
															${plan.price}
															<span className="text-sm font-normal text-gray-600">
																/month
															</span>
														</div>

														<div className="text-lg font-medium text-gray-700">
															{plan.credits.toLocaleString()}{' '}
															{quotaType === 'lead'
																? 'leads'
																: 'emails'}
															/month
														</div>

														{plan.description && (
															<p className="text-sm text-gray-600">
																{plan.description}
															</p>
														)}

														<div className="flex items-center justify-center pt-2">
															{isSelected ? (
																<div className="flex items-center gap-1 text-emerald-600">
																	<Check className="w-4 h-4" />
																	<span className="text-sm font-medium">
																		Selected
																	</span>
																</div>
															) : (
																<span className="text-sm text-gray-500">
																	Click to select
																</span>
															)}
														</div>
													</div>
												</CardContent>
											</Card>
										);
									})}
								</div>
							</div>

							{/* Selected Plan Summary in Content Area */}
							{selectedPlan && (
								<Card className="bg-gray-50 border-gray-200">
									<CardContent className="p-4">
										{(() => {
											const selected = plans.find(
												(p) => p.id === selectedPlan
											);
											const isCurrentPlan =
												selectedPlan === currentSubscription?.plan?.id ||
												(!currentSubscription && selected?.name === 'Free');

											if (selected) {
												return (
													<div className="flex items-center justify-between">
														<div>
															<div className="text-sm font-medium text-gray-900">
																{isCurrentPlan
																	? 'Current Plan Selected'
																	: 'Selected Plan'}
															</div>
															<div className="text-sm text-gray-600">
																{selected.name} - ${selected.price}
																/month •{' '}
																{selected.credits.toLocaleString()}{' '}
																{quotaType === 'lead'
																	? 'leads'
																	: 'emails'}
															</div>
														</div>
														<Check className="w-5 h-5 text-emerald-500" />
													</div>
												);
											}
											return null;
										})()}
									</CardContent>
								</Card>
							)}
						</div>
					)}
				</div>

				<DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
					<Button variant="outline" onClick={onClose} disabled={upgrading}>
						Cancel
					</Button>
					<Button
						onClick={handleUpgrade}
						disabled={
							!selectedPlan ||
							upgrading ||
							selectedPlan === currentSubscription?.plan?.id ||
							(!currentSubscription &&
								plans.find((p) => p.id === selectedPlan)?.name === 'Free')
						}
						className="bg-emerald-600 hover:bg-emerald-700"
					>
						{upgrading ? (
							<div className="flex items-center gap-2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
								Upgrading...
							</div>
						) : selectedPlan === currentSubscription?.plan?.id ? (
							'Current Plan'
						) : !currentSubscription &&
						  plans.find((p) => p.id === selectedPlan)?.name === 'Free' ? (
							'Current Plan'
						) : (
							'Upgrade Plan'
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
